import {
  ChangeEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Box, Stack, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';

import {
  selectScenarioDaysOfWeekInfo,
  selectScenarioEnd,
  selectScenarioIsSaving,
  selectScenarioName,
  selectScenarioStart,
  setScenarioDaysOfWeek,
  setScenarioEnd,
  setScenarioName,
  setScenarioStart,
  useGetScenariosQuery,
} from 'state/detourSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';

import dayjs from 'dayjs';
import { TimeSelector } from 'features/common/DateTimeSelector/TimeSelector';
import {
  calculateEndHourAndLabel,
  convertTo12Hour,
  convertTo24HourString,
} from 'features/common/DateTimeSelector/utils';
import StlHelperTxt from 'features/common/StlHelperTxt';
import StlSidebarFormWrapper from 'features/common/StlSidebarForm/StlSidebarForm';
import { DAYS_OF_WEEK, DayOfWeek } from 'features/common/datetimeUtils';
import { useDebouncedValue } from 'features/common/hooks/useDebouncedValue';
import { updateNameError } from 'features/common/utils';
import { InfoWalkmeCrumb } from 'features/info_hover/InfoHover';

import { ScenarioSetupUIMode } from '../../appConstants';
import { Scenario } from '../../appTypes';
import {
  selectCurrentProjectInfo,
  selectCurrentScenarioId,
  selectProject,
  selectScenarioSetupUIMode,
  selectSupportMinutes,
} from '../../state/workflowSlice';
import { TaskBarSelect } from '../task_bar/TaskBarSelect';
import { DetourLabel } from './DetourLabel';
import SliderCapacity from './DetourTaskBar/SliderCapacity';
import ScenarioCreateButtons from './ScenarioCreateButtons';
import { ScenarioMonthPicker } from './ScenarioMonthPicker';
import {
  MAX_CLOSURE_HOURS,
  closureDurationHours,
  getValidEndDays,
  inferEndDayFromDuration,
  normalizeDaysOfWeeks,
  resolveEndTimeForEndDayChange,
  validateClosureDuration,
} from './utils';

import styles from './WorkflowDetour.module.css';

const DEFAULT_START = '18:00';
const DEFAULT_END = '06:00';

function getMostRecentlyRanScenario(
  scenarios: Scenario[],
): Scenario | undefined {
  return (scenarios ?? []).reduce<Scenario>(
    (mostRecent, scenario) =>
      !mostRecent || dayjs(scenario.created_at).isAfter(mostRecent.created_at)
        ? scenario
        : mostRecent,
    undefined,
  );
}

export function ScenarioSettings() {
  const dispatch = useAppDispatch();
  const scenarioName = useAppSelector(selectScenarioName);
  const currentScenarioId = useAppSelector(selectCurrentScenarioId);
  const { daysOfWeek, startDay, endDay } = useAppSelector(
    selectScenarioDaysOfWeekInfo,
  );
  const closureStart = useAppSelector(selectScenarioStart);
  const closureEnd = useAppSelector(selectScenarioEnd);
  const projectSlug = useAppSelector(selectProject);
  const uiMode = useAppSelector(selectScenarioSetupUIMode);
  const scenarioIsSaving = useAppSelector(selectScenarioIsSaving);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const supportMins = useAppSelector(selectSupportMinutes);
  const { currentData: pastScenarios, isSuccess: scenariosLoaded } =
    useGetScenariosQuery(projectSlug, {
      skip: !projectSlug,
    });

  const startDayIndex = userProject?.uses_middle_east_day_types ? 3 : 4;

  const defaultStartDay = DAYS_OF_WEEK[startDayIndex];
  const defaultEndDay = DAYS_OF_WEEK[startDayIndex + 1];

  const defaultClosureTimes = useMemo(() => {
    const scenarios = pastScenarios?.scenarios ?? [];
    const mostRecent = getMostRecentlyRanScenario(scenarios);

    if (mostRecent?.closure_start && mostRecent?.closure_end) {
      return {
        start: mostRecent.closure_start,
        end: mostRecent.closure_end,
      };
    }

    return { start: DEFAULT_START, end: DEFAULT_END };
  }, [pastScenarios?.scenarios]);

  const hasDaysOfWeek = Boolean(daysOfWeek?.length);

  const selectedStartDay = startDay ?? defaultStartDay;
  const selectedEndDay = endDay ?? defaultEndDay;

  const startHour = closureStart
    ? convertTo12Hour(closureStart)
    : convertTo12Hour(defaultClosureTimes.start);

  const endHour = closureEnd
    ? convertTo12Hour(closureEnd)
    : convertTo12Hour(defaultClosureTimes.end);

  const closureStart24 = closureStart ?? defaultClosureTimes.start;
  const closureEnd24 = closureEnd ?? defaultClosureTimes.end;

  const duration = useMemo(
    () =>
      closureDurationHours(
        selectedStartDay,
        selectedEndDay,
        closureStart24,
        closureEnd24,
      ),
    [selectedStartDay, selectedEndDay, closureStart24, closureEnd24],
  );

  const validEndDayOptions = useMemo(
    () => getValidEndDays(selectedStartDay, closureStart24),
    [selectedStartDay, closureStart24],
  );

  const durationError = useMemo(
    () =>
      closureStart && closureEnd
        ? validateClosureDuration(
            selectedStartDay,
            selectedEndDay,
            closureStart24,
            closureEnd24,
          )
        : undefined,
    [
      selectedStartDay,
      selectedEndDay,
      closureStart,
      closureEnd,
      closureStart24,
      closureEnd24,
    ],
  );

  const getCandidateDurationHours = useCallback(
    (endTime12: string) =>
      closureDurationHours(
        selectedStartDay,
        selectedEndDay,
        closureStart24,
        convertTo24HourString(endTime12),
      ),
    [selectedStartDay, selectedEndDay, closureStart24],
  );

  const [name, setName] = useState(() => scenarioName ?? '');
  const debouncedName = useDebouncedValue(name, 400);
  const [hours, setHours] = useState(() => duration);
  const [touched, setTouched] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);

  const editable =
    uiMode === ScenarioSetupUIMode.Edit ||
    uiMode === ScenarioSetupUIMode.Create;

  const disabled = !editable || scenarioIsSaving;

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (uiMode !== ScenarioSetupUIMode.Create || !scenariosLoaded) return;

    if (!closureStart) dispatch(setScenarioStart(defaultClosureTimes.start));
    if (!closureEnd) dispatch(setScenarioEnd(defaultClosureTimes.end));
  }, [
    uiMode,
    scenariosLoaded,
    closureStart,
    closureEnd,
    defaultClosureTimes.start,
    defaultClosureTimes.end,
  ]);

  useEffect(() => {
    if (!hasDaysOfWeek) {
      dispatch(
        setScenarioDaysOfWeek(
          normalizeDaysOfWeeks([defaultStartDay, defaultEndDay]),
        ),
      );
    }
  }, [hasDaysOfWeek]);

  useEffect(() => {
    setHours(duration);
  }, [duration]);

  const syncEndDayFromDuration = useCallback(
    (durationHours: number) => {
      if (durationHours <= 0) return;
      const nextEnd = inferEndDayFromDuration(
        selectedStartDay,
        startHour,
        durationHours,
      );
      if (nextEnd !== selectedEndDay) {
        dispatch(
          setScenarioDaysOfWeek(
            normalizeDaysOfWeeks([selectedStartDay, nextEnd]),
          ),
        );
      }
    },
    [selectedStartDay, startHour, selectedEndDay],
  );

  const hoursRef = useRef(hours);
  hoursRef.current = hours;

  const handleSetHours = useCallback(
    (value: SetStateAction<number>) => {
      const nextHours =
        typeof value === 'function' ? value(hoursRef.current) : value;
      if (hoursRef.current === nextHours) return;

      const { endHour: nextEnd12 } = calculateEndHourAndLabel(
        startHour,
        nextHours,
      );
      syncEndDayFromDuration(nextHours);
      dispatch(setScenarioEnd(convertTo24HourString(nextEnd12)));
      setHours(nextHours);
    },
    [startHour, syncEndDayFromDuration],
  );

  // Unlikely event: Sync from store when scenario changes (not during typing)
  useEffect(() => {
    if (!editing) setName(scenarioName ?? '');
  }, [scenarioName, currentScenarioId, editing]);

  useEffect(() => {
    if (!editing) return;
    const committed = debouncedName.trim();
    if (committed !== scenarioName) dispatch(setScenarioName(committed));
  }, [debouncedName, editing, scenarioName]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Ctrl + Z on map triggered handleChange function - couldn't eliminate an underlying cause
    const isFocused = inputRef.current === document.activeElement;
    if (!isFocused) return;
    setName(e.target.value);
    if (!editing) setEditing(true);
  };

  const handleBlur = () => {
    setTouched(true);
    const committed = name.trim();
    if (committed !== scenarioName) dispatch(setScenarioName(committed));
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const existingNames = useMemo(() => {
    const scenarios = pastScenarios?.scenarios ?? [];

    const filtered =
      uiMode === ScenarioSetupUIMode.Create
        ? scenarios
        : scenarios.filter((s) => s.scenario_id !== currentScenarioId);

    return filtered.map((s) => s.scenario_name);
  }, [pastScenarios?.scenarios, uiMode, currentScenarioId]);

  const nameError = updateNameError(undefined, name, existingNames, touched);

  const updateScenarioDaysOfWeek = (start: DayOfWeek, end: DayOfWeek) => {
    dispatch(setScenarioDaysOfWeek(normalizeDaysOfWeeks([start, end])));
  };

  const handleStartDayChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextStart = event.target.value as DayOfWeek;
    const nextEnd = inferEndDayFromDuration(nextStart, startHour, hours);
    updateScenarioDaysOfWeek(nextStart, nextEnd);
  };

  const handleEndDayChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextEndDay = event.target.value as DayOfWeek;
    const { closureEnd: nextEnd, durationHours } =
      resolveEndTimeForEndDayChange(
        selectedStartDay,
        nextEndDay,
        closureStart24,
        closureEnd24,
      );
    updateScenarioDaysOfWeek(selectedStartDay, nextEndDay);
    if (closureEnd !== nextEnd) {
      dispatch(setScenarioEnd(nextEnd));
      setHours(durationHours);
    }
  };

  const hasError = nameError ?? durationError;
  const startIdx = userProject?.uses_middle_east_day_types ? 6 : 0;
  const options = [
    ...DAYS_OF_WEEK.slice(startIdx),
    ...DAYS_OF_WEEK.slice(0, startIdx),
  ];

  return (
    <StlSidebarFormWrapper
      btns={
        editable && (
          <ScenarioCreateButtons
            disableRun={Boolean(hasError) || disabled}
            disableClear={disabled}
          />
        )
      }
    >
      <Stack display="flex" gap={0.5}>
        <DetourLabel lbl="Name" />
        <TextField
          inputRef={inputRef}
          placeholder="E.g., East-West Rose Road"
          value={name}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          size="small"
          className={!name ? styles.placeholder : ''}
          error={Boolean(nameError)}
          helperText={
            nameError ? <StlHelperTxt variant="error" txt={nameError} /> : null
          }
          InputProps={{
            classes: {
              input: styles.nameInput,
            },
          }}
        />
      </Stack>
      <ScenarioMonthPicker disabled={disabled} />
      <Stack display="flex" gap={1}>
        <Stack display="flex">
          <Box display="flex">
            <DetourLabel lbl="Closure Duration" />
            <InfoWalkmeCrumb elId="closure-duration" />
          </Box>
          <Typography
            variant="caption"
            color="rgba(245, 245, 245, 0.7)"
            className={styles.pl4}
          >
            Specify a duration of up to 60 hours
          </Typography>
        </Stack>
        <TimeSelector
          startExtra={
            <TaskBarSelect
              classes={{
                paper: styles.mL8,
                menuItem: styles.fS14,
              }}
              value={selectedStartDay}
              onChange={handleStartDayChange}
              fullWidth
              values={options.map((d) => ({ value: d, label: d.slice(0, 3) }))}
              disabled={disabled}
            />
          }
          endExtra={
            <TaskBarSelect
              classes={{
                paper: styles.mL8,
                menuItem: styles.fS14,
              }}
              value={selectedEndDay}
              onChange={handleEndDayChange}
              fullWidth
              values={options.map((d) => ({
                value: d,
                label: d.slice(0, 3),
                disabled: !validEndDayOptions.includes(d),
              }))}
              disabled={disabled}
            />
          }
          detourVersion
          canEditEndTime
          minBins={supportMins}
          amPmVersion
          maxHours={MAX_CLOSURE_HOURS}
          startHour={startHour}
          setStartHour={(val) =>
            dispatch(setScenarioStart(convertTo24HourString(val)))
          }
          endHour={endHour}
          setEndHour={(val) =>
            dispatch(setScenarioEnd(convertTo24HourString(val)))
          }
          hours={hours}
          setHours={handleSetHours}
          endNotBefore={
            selectedStartDay === selectedEndDay ? startHour : undefined
          }
          getCandidateDurationHours={getCandidateDurationHours}
        />
        {durationError ? (
          <StlHelperTxt variant="error" txt={durationError} />
        ) : null}
      </Stack>
      <SliderCapacity showSlider={editable} />
    </StlSidebarFormWrapper>
  );
}
